/**
 * Preview-mode plumbing for the public bundle.
 *
 * "Preview on site" lets the admin platform render the REAL public bundle on
 * a draft box that isn't published yet. Activated by `?preview=<flowId>` (or
 * the legacy `?preview=1` for "preview mode on" with no specific draft).
 *
 * How a draft is loaded (N2b): the bundle reads the flow id from the query
 * (`getPreviewFlowId`) and fetches that draft's FULL doc from the admin's
 * role-gated `GET /api/flows/<id>/preview` endpoint WITH credentials
 * (`fetchPreviewFlow`), then renders it as the active flow. Submission stays
 * disabled in preview mode (the checkout submit handler short-circuits) and
 * the PreviewBanner shows.
 *
 * Security model: the credentialed fetch is authorised by the admin session
 * cookie against the role-gated endpoint, so an unreleased draft never leaks
 * to the public read path. The fetch target origin must be a trusted admin
 * origin (`getPreviewOrigin`, derived from `VITE_ADMIN_PREVIEW_ORIGIN`).
 *
 * History: the original K+4 design used a `postMessage` channel (iframe
 * preview pane). N2a removed the admin sender (LivePreviewPane) and
 * N2-closeout removed the now-dead public receiver/handshake; the
 * credentialed fetch above is the live mechanism.
 */

function readPreviewParam(search?: string): string | null {
  if (typeof window === 'undefined' && !search) return null;
  const qs = search ?? window.location.search;
  try {
    return new URLSearchParams(qs).get('preview');
  } catch {
    return null;
  }
}

/** "off" values that keep their legacy meaning of "no preview". */
const PREVIEW_OFF_VALUES = new Set(['', '0', 'false']);

/**
 * Detect preview mode purely from the URL query string. Safe to call in
 * non-browser contexts (returns false during SSR / tests without a window).
 *
 * N2b: preview mode is on for ANY non-`0`/`false` value — including a flow id
 * (`?preview=<flowId>`), not just the legacy `1`/`true`. The flow id (when
 * present) is read separately by `getPreviewFlowId`.
 */
export function isPreviewMode(search?: string): boolean {
  const v = readPreviewParam(search);
  if (v === null) return false;
  return !PREVIEW_OFF_VALUES.has(v);
}

/**
 * The draft flow id to preview, extracted from `?preview=<flowId>`. Returns
 * `null` for the legacy boolean values (`1` / `true`) and for the off values,
 * since those carry no flow id (the bundle just enters preview mode + shows
 * the banner). When a flow id IS present, the ContentProvider fetches that
 * draft's FULL doc from the admin preview endpoint and renders it.
 */
export function getPreviewFlowId(search?: string): string | null {
  const v = readPreviewParam(search);
  if (v === null) return null;
  if (PREVIEW_OFF_VALUES.has(v)) return null;
  if (v === '1' || v === 'true') return null;
  return v;
}

/**
 * Read the configured admin origin. Vite exposes env vars on
 * `import.meta.env`; we tolerate the var being unset by falling back to a
 * developer-friendly localhost allowlist.
 *
 * Tests stub the value via `setConfiguredOriginForTest`; the production
 * code-path reads `import.meta.env.VITE_ADMIN_PREVIEW_ORIGIN`.
 */
let __testOriginOverride: string | undefined;

export function setConfiguredOriginForTest(value: string | undefined): void {
  __testOriginOverride = value;
}

function readConfiguredOrigin(): string | undefined {
  if (__testOriginOverride !== undefined) return __testOriginOverride;
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const meta = (import.meta as any).env as Record<string, string> | undefined;
    return meta?.['VITE_ADMIN_PREVIEW_ORIGIN'];
  } catch {
    return undefined;
  }
}

/**
 * Default localhost origins accepted in development. Production builds
 * should set `VITE_ADMIN_PREVIEW_ORIGIN` explicitly.
 *
 * M+5.1: When the production CI sets VITE_ADMIN_PREVIEW_ORIGIN, the env
 * value wins and the dev fallbacks below are NOT consulted (see
 * `getAllowedAdminOrigins`). So in prod the preview iframe accepts
 * postMessages only from the configured admin origin
 * (https://admin.the-brandbox-studio.ddg-europe.com).
 */
const DEV_FALLBACK_ORIGINS = [
  'http://localhost:3001',
  'http://localhost:3000',
  'http://127.0.0.1:3001',
  'http://127.0.0.1:3000',
];

export function getAllowedAdminOrigins(): string[] {
  const configured = readConfiguredOrigin();
  if (configured) {
    return configured
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }
  return [...DEV_FALLBACK_ORIGINS];
}

// ---------------------------------------------------------------------------
// N2b — "Preview on site" draft fetch
// ---------------------------------------------------------------------------

/**
 * The admin origin the credentialed preview fetch targets. Reuses the same
 * `VITE_ADMIN_PREVIEW_ORIGIN` env that gates the postMessage allowlist (the
 * trusted admin origin); the first entry of a comma list is used. Falls back
 * to the local admin dev origin so the developer flow works out of the box.
 */
export function getPreviewOrigin(): string {
  const origins = getAllowedAdminOrigins();
  return origins[0] ?? 'http://localhost:3001';
}

/**
 * Fetch a DRAFT flow's FULL content from the admin's role-gated preview
 * endpoint (`GET <adminOrigin>/api/flows/<id>/preview`) WITH credentials, so
 * the admin session cookie authorises the read. This is the public half of
 * the "Preview on site" seam (the admin half — the endpoint — shipped in
 * N2a): the configurator renders the returned doc as the active flow while
 * submission stays disabled.
 *
 * Returns `null` on any non-OK response (401/403/404) or network error — the
 * caller (ContentProvider / configurator) fails gracefully, never crashes and
 * never falls back to Spring.
 *
 * NOTE: for the cross-origin credentialed GET to succeed in prod, the public
 * bundle origin must be in the admin's `WRITE_CORS_ALLOWED_ORIGINS` so the
 * exact ACAO origin is reflected (operator task — carried to N4).
 */
export async function fetchPreviewFlow(id: string): Promise<unknown | null> {
  if (!id) return null;
  const origin = getPreviewOrigin().replace(/\/+$/, '');
  try {
    const res = await fetch(`${origin}/api/flows/${encodeURIComponent(id)}/preview`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return null;
    return (await res.json()) as unknown;
  } catch {
    return null;
  }
}
